import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiDollarSign, FiShield, FiClock, FiCheckCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import { RETENTION_LEDGER_DATA } from "./worksReportsData";

const SubcontractorRetentionLedgerReport = () => {
  const gridRef = useRef(null);
  const [data] = useState(RETENTION_LEDGER_DATA);
  const [searchTerm, setSearchTerm] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [contractorFilter, setContractorFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalWithheld = data.reduce((s, r) => s + (r.retentionWithheld || 0), 0);
    const totalReleased = data.reduce((s, r) => s + (r.retentionReleased || 0), 0);
    const netBalance = data.reduce((s, r) => s + (r.netRetentionBalance || 0), 0);

    return {
      totalWithheld,
      totalReleased,
      netBalance,
      totalAccounts: data.length,
    };
  }, [data]);

  const uniqueProjects = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.project).filter(Boolean)));
  }, [data]);

  const uniqueContractors = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.subcontractor).filter(Boolean)));
  }, [data]);

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Subcontractor Name",
        field: "subcontractor",
        colId: "subcontractor",
        flex: 1.8,
        minWidth: 200,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#0f172a" }}>{p.value}</span>,
      },
      {
        headerName: "Linked Work Order",
        field: "workOrderRef",
        colId: "workOrderRef",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => (
          <span
            style={{
              fontWeight: 700,
              fontFamily: "monospace",
              color: "var(--color-primary, #0b6462)",
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "Project / Site",
        field: "project",
        colId: "project",
        flex: 1.4,
        minWidth: 160,
      },
      {
        headerName: "Contract Value (PKR)",
        field: "contractValue",
        colId: "contractValue",
        flex: 1.5,
        minWidth: 160,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "PKR 0"),
      },
      {
        headerName: "Cumulative Retention (PKR)",
        field: "retentionWithheld",
        colId: "retentionWithheld",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "#d97706" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Released to Date (PKR)",
        field: "retentionReleased",
        colId: "retentionReleased",
        flex: 1.4,
        minWidth: 150,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "#16a34a" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Net Held in Escrow (PKR)",
        field: "netRetentionBalance",
        colId: "netRetentionBalance",
        flex: 1.6,
        minWidth: 170,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0b6462" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Completion / Handover",
        field: "completionDate",
        colId: "completionDate",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => <span style={{ color: "#334155" }}>{p.value}</span>,
      },
      {
        headerName: "Target Release Date",
        field: "targetReleaseDate",
        colId: "targetReleaseDate",
        flex: 1.4,
        minWidth: 150,
        cellRenderer: (p) => (
          <span
            style={{
              fontWeight: 600,
              color: "#0284c7",
              background: "#f0f9ff",
              padding: "2px 8px",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            {p.value}
          </span>
        ),
      },
    ];
  }, []);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matches =
          (row.subcontractor && row.subcontractor.toLowerCase().includes(q)) ||
          (row.workOrderRef && row.workOrderRef.toLowerCase().includes(q)) ||
          (row.project && row.project.toLowerCase().includes(q));
        if (!matches) {
          return false;
        }
      }

      if (projectFilter !== "all" && row.project !== projectFilter) {
        return false;
      }

      if (contractorFilter !== "all" && row.subcontractor !== contractorFilter) {
        return false;
      }

      return true;
    });
  }, [data, searchTerm, projectFilter, contractorFilter]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
    }),
    [],
  );

  const toggleColumn = useCallback((colId) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      if (gridRef.current?.api) {
        gridRef.current.api.setColumnsVisible([colId], next[colId]);
      }
      return next;
    });
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Subcontractor_Retention_Ledger_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("Retention ledger exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL RETENTION WITHHELD"
            value={`PKR ${(kpis.totalWithheld / 1000).toFixed(0)}K`}
            subtitle="Security deducted across active contracts"
            icon={FiShield}
            color="#d97706"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="RETENTION RELEASED"
            value={`PKR ${(kpis.totalReleased / 1000).toFixed(0)}K`}
            subtitle="Disbursed post-defect liability clearance"
            icon={FiCheckCircle}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="NET HELD IN ESCROW"
            value={`PKR ${(kpis.netBalance / 1000).toFixed(0)}K`}
            subtitle="Current retention liability balance"
            icon={FiDollarSign}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="RETENTION ACCOUNTS"
            value={`${kpis.totalAccounts} Thekedars`}
            subtitle="Monitored for maintenance guarantee"
            icon={FiClock}
            color="#0284c7"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (Search First, then Date, then other filters) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Retention Money Ledger"
            >
              Retention Money Ledger
            </div>
            {/* Search Filter FIRST */}
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search contractor, WO, project..."
            />

            {/* Date Filter SECOND */}
            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            {/* Project Filter THIRD */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                >
                  <option value="all">All Projects</option>
                  {uniqueProjects.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <label>Project / Site</label>
              </div>
            </div>

            {/* Contractor Filter */}
            <div className="d-flex align-items-center" style={{ maxWidth: "220px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={contractorFilter}
                  onChange={(e) => setContractorFilter(e.target.value)}
                >
                  <option value="all">All Subcontractors</option>
                  {uniqueContractors.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <label>Subcontractor</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              animateRows={true}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubcontractorRetentionLedgerReport;
